/**
 * AfterDoc 내보내기 파일 파서 (순수 함수, 네트워크 호출 없음)
 *
 * 입력 파일 두 종류:
 *  - data-store.json      : "<type>:<id>" 키의 플랫 객체. manual / automation / automation-message 등을 담는다.
 *  - collect-api-bodies.json : { t, scenario, method, url, status, ms, body } 레코드 배열.
 *
 * 자동화 -> 메시지 연결(어떤 메시지가 어떤 자동화 소속인지):
 *  1순위) automation-message.data.automationId — 수집기가 심어주면 data-store.json 하나로 완결.
 *  2순위) automationId 가 없을 때만 collect-api-bodies.json 의 요청 URL(algorithmId)로 복원.
 *         이때도 필요한 건 자동화 연결 요청 몇 건(48)뿐이며, 채팅 bodies(수 GB)는 무관하다.
 */

// 자동화 메시지 목록 API 의 URL 표식
const MESSAGES_URL_MARK = "/automations/el/algorithms/messages";
const ALGORITHM_ID_RE = /[?&]algorithmId=([0-9a-fA-F]{24})/;
const VARIABLE_RE = /\{\{\s*([^{}]+?)\s*\}\}/g;

/**
 * File 객체를 JSON 으로 읽는다 (브라우저 전용).
 * @param {File} file
 * @returns {Promise<any>}
 */
export function readJsonFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        resolve(JSON.parse(reader.result));
      } catch (error) {
        reject(new Error(`${file.name} 파일이 올바른 JSON 형식이 아닙니다.`));
      }
    };
    reader.onerror = () => reject(new Error(`${file.name} 파일을 읽지 못했습니다.`));
    reader.readAsText(file);
  });
}

/**
 * 본문에서 {{변수}} 목록을 추출한다 (중복 제거).
 * @param {string} content
 * @returns {string[]}
 */
export function extractVariables(content) {
  const found = new Set();
  if (!content) return [];

  VARIABLE_RE.lastIndex = 0;
  let matched = VARIABLE_RE.exec(content);
  while (matched !== null) {
    found.add(matched[1]);
    matched = VARIABLE_RE.exec(content);
  }
  return Array.from(found);
}

/**
 * data-store 에서 특정 타입의 data 객체들만 뽑는다.
 * @param {Object} dataStore
 * @param {string} type
 * @returns {Object[]}
 */
function entriesOfType(dataStore, type) {
  return Object.entries(dataStore || {})
    .filter(([key, entry]) => (entry && entry.type ? entry.type : key.split(":")[0]) === type)
    .map(([, entry]) => (entry ? entry.data : null))
    .filter(Boolean);
}

/**
 * collect-api-bodies 에서 automationId -> 메시지 배열 맵을 복원한다.
 * @param {Array} apiBodies
 * @returns {Map<string, Object[]>}
 */
export function buildAutomationMessageMap(apiBodies) {
  const messageMap = new Map();

  for (const record of apiBodies || []) {
    const url = record && record.url ? record.url : "";
    if (!url.includes(MESSAGES_URL_MARK)) continue;

    const matched = url.match(ALGORITHM_ID_RE);
    if (!matched) continue;

    let messages;
    try {
      const parsedBody = JSON.parse(record.body || "{}");
      messages = parsedBody ? parsedBody.data : null;
    } catch (error) {
      continue;
    }
    if (!Array.isArray(messages)) continue;

    // 같은 자동화에 대한 응답이 여러 번 수집된 경우 가장 많은 메시지를 담은 응답을 채택
    const previous = messageMap.get(matched[1]);
    if (!previous || messages.length > previous.length) {
      messageMap.set(matched[1], messages);
    }
  }

  return messageMap;
}

// 자동화 메시지가 부모 자동화를 가리키는 필드. 수집기가 심어주는 값(automationId)이 정식이고,
// AfterDoc API 쿼리명(algorithmId)을 쓰는 경우도 대비해 둘 다 인정한다.
function parentAutomationIdOf(msg) {
  return (msg && (msg.automationId || msg.algorithmId)) || null;
}

/**
 * data-store 의 automation-message 를 automationId 로 묶어 automationId -> 메시지 배열 맵을 만든다.
 * automationId 가 심어져 있으면 collect-api-bodies.json 없이 data-store.json 하나로 연결이 복원된다.
 * @param {Object} dataStore
 * @returns {Map<string, Object[]>}
 */
export function buildAutomationMessageMapFromStore(dataStore) {
  const messageMap = new Map();
  for (const msg of entriesOfType(dataStore, "automation-message")) {
    const parentId = parentAutomationIdOf(msg);
    if (!parentId) continue;
    const list = messageMap.get(parentId);
    if (list) list.push(msg);
    else messageMap.set(parentId, [msg]);
  }
  return messageMap;
}

/**
 * 자동화 -> 메시지 연결을 data-store.json 하나로 풀 수 있는지 판별한다.
 * needsApiBodies 가 true 인 경우에만 collect-api-bodies.json 을 추가로 요구하면 된다.
 * @param {Object} dataStore
 * @returns {{hasAutomations:boolean, automationCount:number, messageCount:number, linkedCount:number, fromStore:boolean, needsApiBodies:boolean}}
 */
export function automationLinkageStatus(dataStore) {
  const automations = entriesOfType(dataStore, "automation");
  const messages = entriesOfType(dataStore, "automation-message");
  const linkedCount = messages.filter((m) => parentAutomationIdOf(m)).length;
  const hasAutomations = automations.length > 0;
  // 메시지가 있고 전부 automationId 를 가진 경우에만 data-store 단독 복원이 안전하다.
  const fromStore = messages.length > 0 && linkedCount === messages.length;
  return {
    hasAutomations,
    automationCount: automations.length,
    messageCount: messages.length,
    linkedCount,
    fromStore,
    // 자동화가 있는데 store 로 연결이 안 풀릴 때만 두 번째 파일이 필요하다.
    needsApiBodies: hasAutomations && !fromStore,
  };
}

/**
 * AfterDoc 내보내기 데이터를 crm-api 마이그레이션 payload 형태로 정규화한다.
 * @param {Object} dataStore  data-store.json 파싱 결과
 * @param {Array} apiBodies   collect-api-bodies.json 파싱 결과
 * @returns {{tags: Object[], templates: Object[], scenarios: Object[], stats: Object}}
 */
export function parseAfterdocExport(dataStore, apiBodies) {
  const manuals = entriesOfType(dataStore, "manual");
  const automations = entriesOfType(dataStore, "automation");
  // 1순위 store(automationId) 우선, 없으면 apiBodies(algorithmId) 로 메운다. store 값이 우선.
  const messageMap = buildAutomationMessageMap(apiBodies);
  for (const [automationId, messages] of buildAutomationMessageMapFromStore(dataStore)) {
    messageMap.set(automationId, messages);
  }

  // 태그: manual 의 tagId 와 automation.treatmentTags 의 합집합 (태그 id 기준 중복 제거)
  const tagMap = new Map();
  for (const manual of manuals) {
    if (manual.tagId && !tagMap.has(manual.tagId)) {
      tagMap.set(manual.tagId, {
        sourceId: manual.tagId,
        name: manual.tagName || "",
        color: manual.tagColor || "",
      });
    }
  }
  for (const automation of automations) {
    for (const tag of automation.treatmentTags || []) {
      if (tag && tag._id && !tagMap.has(tag._id)) {
        tagMap.set(tag._id, {
          sourceId: tag._id,
          name: tag.categoryName || "",
          color: tag.color || "",
        });
      }
    }
  }

  // 주의사항 템플릿: 주의사항 사용 + 내용 존재 + 카카오 템플릿 정보 존재
  const cautionTemplates = manuals
    .filter((manual) => manual.usingCautionMessage === true && manual.cautionMessage && manual.kakaoTemplateInfo)
    .map((manual) => ({
      sourceId: manual.tagId,
      kind: "caution",
      name: manual.kakaoTemplateInfo.templateName || manual.tagName,
      content: manual.cautionMessage,
      categoryCode: manual.kakaoTemplateInfo.categoryCode,
      templateMessageType: manual.kakaoTemplateInfo.templateMessageType,
      templateEmphasizeType: manual.kakaoTemplateInfo.templateEmphasizeType,
      sourceTemplateCode: manual.kakaoTemplateInfo.templateCode,
      // AfterDoc 이 이 템플릿을 등록할 때 쓴 발신프로필.
      // 우리 senderKey 와 같은 발신프로필이면 이미 DKT 에 올라가 있다는 뜻이라,
      // 서버가 재등록 대신 기존 templateCode 를 채택한다.
      sourceSenderKey: manual.kakaoTemplateInfo.senderKey,
      tagSourceIds: [manual.tagId],
      rejectedBefore: false,
    }));

  // 자동화 템플릿 + 시나리오: 복원한 메시지 맵 기준
  const automationTemplates = [];
  const scenarios = automations.map((automation) => {
    const messages = messageMap.get(automation._id) || [];
    const tagSourceIds = automation.appliedTreatmentTagIds || [];

    for (const msg of messages) {
      automationTemplates.push({
        sourceId: msg.id,
        kind: "automation",
        name: msg.name,
        content: msg.content && msg.content.text ? msg.content.text : "",
        tagSourceIds,
        rejectedBefore: Boolean(msg.rejectedReasonForAlimtalkTemplate),
      });
    }

    return {
      sourceId: automation._id,
      name: automation.name,
      isActive: Boolean(automation.isActive),
      tagSourceIds,
      messages: messages.map((msg) => ({
        sourceId: msg.id,
        name: msg.name,
        sendingType: msg.sendingType,
        daysAfter: msg.daysAfter,
        timeOfDay: msg.timeOfDay,
      })),
    };
  });

  const tags = Array.from(tagMap.values());
  const templates = [...cautionTemplates, ...automationTemplates];

  return {
    tags,
    templates,
    scenarios,
    stats: {
      templates: templates.length,
      scenarios: scenarios.length,
      tags: tags.length,
      activeScenarios: scenarios.filter((scenario) => scenario.isActive).length,
    },
  };
}

/**
 * 파싱 결과에서 AfterDoc 이 템플릿을 등록할 때 쓴 발신프로필 senderKey 목록을 뽑는다 (중복 제거).
 * 주의사항 템플릿에만 sourceSenderKey 가 있고 실제로는 모두 같은 값 하나를 공유한다.
 * 이 값과 같은 발신프로필을 고르면 서버가 기존 템플릿을 그대로 채택할 수 있다.
 * @param {{templates: Object[]}} parsed parseAfterdocExport 결과
 * @returns {string[]}
 */
export function extractSourceSenderKeys(parsed) {
  const found = new Set();
  for (const template of (parsed && parsed.templates) || []) {
    if (template.sourceSenderKey) found.add(template.sourceSenderKey);
  }
  return Array.from(found);
}

// 템플릿 종류 표기
export const TEMPLATE_KIND_TEXT_MAP = {
  caution: "주의사항",
  automation: "자동화",
};

export const TEMPLATE_KIND_COLOR_MAP = {
  caution: "purple",
  automation: "blue",
};
